import React, { useContext, useEffect, useState } from "react";
import {
  Card,
  Col,
  DatePicker,
  Row,
  Spin,
  Table,
  Typography,
  message,
  Statistic,
  Alert,
} from "antd";
import { AppContext } from "../context/AppContext";
import DoubleEntryService from "../utils/doubleEntryService";
import moment from "moment";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const formatCurrency = (value) =>
  `ZMW ${Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const TrialBalance = () => {
  const { company } = useContext(AppContext);
  const token = localStorage.getItem("token");

  const [loading, setLoading] = useState(true);
  const [trialBalanceData, setTrialBalanceData] = useState(null);
  const [dateRange, setDateRange] = useState([
    moment().startOf("year"),
    moment(),
  ]);

  useEffect(() => {
    fetchTrialBalance();
  }, [company?.id, dateRange]);

  const fetchTrialBalance = async () => {
    if (!company?.id || !token) return;

    setLoading(true);
    try {
      const [startDate, endDate] = dateRange;

      const data = await DoubleEntryService.getTrialBalance({
        company: company.id,
        token,
        startDate: startDate.format("YYYY-MM-DD"),
        endDate: endDate.format("YYYY-MM-DD"),
      });

      setTrialBalanceData(data);
    } catch (error) {
      console.error("Error fetching trial balance:", error);
      message.error("Failed to load trial balance");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Account Code",
      dataIndex: "code",
      key: "code",
      width: 120,
    },
    {
      title: "Account Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Debit",
      dataIndex: "debit",
      key: "debit",
      align: "right",
      width: 150,
      render: (value) => formatCurrency(value),
    },
    {
      title: "Credit",
      dataIndex: "credit",
      key: "credit",
      align: "right",
      width: 150,
      render: (value) => formatCurrency(value),
    },
    {
      title: "Balance",
      dataIndex: "balance",
      key: "balance",
      align: "right",
      width: 150,
      render: (value) => (
        <Text strong style={{ color: value >= 0 ? "#52c41a" : "#ff4d4f" }}>
          {formatCurrency(Math.abs(value))} {value < 0 ? "CR" : "DR"}
        </Text>
      ),
    },
  ];

  const isBalanced =
    trialBalanceData &&
    Math.abs(trialBalanceData.totalDebits - trialBalanceData.totalCredits) <
      0.01;

  return (
    <div className="page-container">
      <Row gutter={[16, 16]} align="middle" style={{ marginBottom: 24 }}>
        <Col xs={24} md={14}>
          <Title level={3} style={{ marginBottom: 0 }}>
            Trial Balance
          </Title>
          <Text type="secondary">
            Summary of all account balances with debits and credits
          </Text>
        </Col>
        <Col xs={24} md={10}>
          <RangePicker
            value={dateRange}
            onChange={(range) => range && setDateRange(range)}
            style={{ width: "100%" }}
          />
        </Col>
      </Row>

      {!loading && trialBalanceData && (
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="Total Debits"
                value={trialBalanceData.totalDebits}
                precision={2}
                prefix="ZMW"
                valueStyle={{ color: "#3f8600" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="Total Credits"
                value={trialBalanceData.totalCredits}
                precision={2}
                prefix="ZMW"
                valueStyle={{ color: "#cf1322" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="Difference"
                value={Math.abs(
                  trialBalanceData.totalDebits - trialBalanceData.totalCredits
                )}
                precision={2}
                prefix="ZMW"
                valueStyle={{
                  color: isBalanced ? "#52c41a" : "#ff4d4f",
                }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {!loading && isBalanced && (
        <Alert
          message="Books are Balanced"
          description="Total debits equal total credits. Your accounting records are in order!"
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {!loading && !isBalanced && trialBalanceData && (
        <Alert
          message="Books are Out of Balance"
          description={`There is a difference of ${formatCurrency(
            Math.abs(
              trialBalanceData.totalDebits - trialBalanceData.totalCredits
            )
          )} between debits and credits. Please review your entries.`}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Card>
        <Spin spinning={loading}>
          {trialBalanceData && (
            <Table
              dataSource={trialBalanceData.accounts}
              columns={columns}
              rowKey="code"
              pagination={false}
              summary={(pageData) => {
                const totalDebits = pageData.reduce(
                  (sum, acc) => sum + acc.debit,
                  0
                );
                const totalCredits = pageData.reduce(
                  (sum, acc) => sum + acc.credit,
                  0
                );

                return (
                  <Table.Summary.Row style={{ backgroundColor: "#fafafa" }}>
                    <Table.Summary.Cell colSpan={2}>
                      <Text strong>TOTAL</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell align="right">
                      <Text strong>{formatCurrency(totalDebits)}</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell align="right">
                      <Text strong>{formatCurrency(totalCredits)}</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell align="right">
                      <Text
                        strong
                        style={{
                          color: isBalanced ? "#52c41a" : "#ff4d4f",
                        }}
                      >
                        {isBalanced ? "✓ BALANCED" : "✗ OUT OF BALANCE"}
                      </Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                );
              }}
            />
          )}

          {!loading && !trialBalanceData?.accounts?.length && (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <Text type="secondary">
                No journal entries found for the selected period.
              </Text>
            </div>
          )}
        </Spin>
      </Card>

      <Card style={{ marginTop: 16 }}>
        <Title level={5}>Understanding the Trial Balance</Title>
        <Text>
          The trial balance is a report that lists all accounts with their
          debit and credit balances. In double-entry accounting:
        </Text>
        <ul>
          <li>
            <Text strong>Total Debits must equal Total Credits</Text> - This
            proves your books are mathematically balanced
          </li>
          <li>
            <Text strong>Asset accounts</Text> normally have debit balances
          </li>
          <li>
            <Text strong>Liability and Equity accounts</Text> normally have
            credit balances (shown as negative)
          </li>
          <li>
            <Text strong>Revenue accounts</Text> have credit balances (shown as
            negative)
          </li>
          <li>
            <Text strong>Expense accounts</Text> have debit balances
          </li>
        </ul>
      </Card>
    </div>
  );
};

export default TrialBalance;
